<template>
  <section class="index-dashboard">
    <div class="index-dashboard__wrapper !tw-justify-center">
      <div class="card-wrap">
        <div class="card card-info">
          <h5 class="head text-success">
            {{ $t('dashboard.available') }}
          </h5>
          <h4
            class
            v-html="available"
          />
        </div>
      </div>
      <div class="card-wrap">
        <div class="card card-info">
          <h5 class="head text-info">
            {{ $t('dashboard.yesterday') }}
          </h5>
          <h4
            class
            v-html="yesterday"
          />
        </div>
      </div>
      <div class="card-wrap">
        <div class="card card-info">
          <h5 class="head text-info">
            {{ $t('dashboard.currentMonth') }}
          </h5>
          <h4
            class
            v-html="thisMonth"
          />
        </div>
      </div>
      <div class="card-wrap">
        <div class="card card-info">
          <h5 class="head text-info">
            {{ $t('dashboard.lastMonth') }}
          </h5>
          <h4
            class
            v-html="lastMonth"
          />
        </div>
      </div>
      <div class="card-wrap">
        <div class="card card-info">
          <h5 class="head text-black">
            {{ $t('dashboard.total') }}
          </h5>
          <h4
            class
            v-html="total"
          />
        </div>
      </div>
    </div>

    <Charts
      ref="charts"
      v-model:currency-label="currencyLabel"
      class="mt-20"
      @chart-period="cacheChart"
    />

    <TableCard
      ref="datatable"
      v-model:sort="sorting"
      v-model:page="page"
      v-model:page-size="pageSize"
      v-model:period="period"
      class="mt-20"
      :loading="loading"
      :columns="columns"
      :currency="currencyLabel"
      :rows="rows"
      :totals="$store.state.report.totals"
      :count="$store.state.report.dashboardRows"
      :formaters="formaters"
      :show-total="true"
      period-picker
      :header="$t('dashboard.dailyStatistics')"
      i18n-path="reports"
      is-static
      extend-height
      dont-fill-empty
      @update:period="period = $event"
      @change-range="fetchDataTable"
      @fetch-data="fetchDataTable"
    />
  </section>
</template>

<script>
import Charts from '@/components/Charts/Charts.vue';
import Formatters from '@/views/Dashboard/CustomReports/formaters';
import UrlMixin from '@/components/Query/url-mixin';
import UrlMixinHooks from '@/components/Query/url-mixin-hooks';
import DimensionsMixin from '@/components/Report/Components/DimensionsMixin';
import InjectPlugins from '@/components/Report/Components/inject-plugins';
import TableCard from '@/components/Cards/TableCard.vue';
import { pageSizeMixin, resolvePageSize } from '@/views/Dashboard/Reports/page_size';
import tableFlowMixinGenerator, { getDefaultPeriod } from '@/views/Dashboard/Reports/tableFlowMixin';
import formatCurrency from '@/mixins/format-currency';
import { fixRange } from '@/views/Dashboard/Reports/dateMixin';

const sizes = {
  1080: 10,
  1440: 10,
};

const viewName = '/dashboard';
const pageLimitProp = 'pageSize';
const pageSize = resolvePageSize(viewName, {
  _default: 10,
  sizes,
});
const dashboardColumns = [
  'impressions_count',
  'visits_count',
  'registrations_count',
  'deposits_first_count',
  'deposits_all_count',
  'deposits_all_sum',
  'net_profit_sum',
  'revenue_sum',
];

const defaultPeriod = getDefaultPeriod();

const defaultParams = {
  page: 1,
  pageSize,
  sort_by: 'date_formatted',
  sort_dir: 'desc',
  chartPeriod: '',
  miniChartPeriod: '',
};

async function fetch(ctx, params = {}) {
  if (localStorage.getItem('auth-token') !== 'null') {
    const sorting = params.sort_by
      ? {
        sort_by: params.sort_by,
        sort_dir: params.sort_dir,
      }
      : ctx.sorting
        ? ctx.sorting
        : {
          sort_by: defaultParams.sort_by,
          sort_dir: defaultParams.sort_dir,
        };

    const requestParms = {
      metrics_format:	'pretty',
      from: ctx.period
        ? ctx.period.from
        : params.period
          ? params.period.from
          : defaultPeriod.from,
      to: ctx.period
        ? ctx.period.to
        : params.period
          ? params.period.to
          : defaultPeriod.to,
      sorting: [sorting],
      metrics: dashboardColumns,
      limit: pageSize,
      offset: 0,
      ...params,
    };

    let fixed = null;
    const period = {
      period: params.period,
      from: requestParms.from,
      to: requestParms.to,
    };

    fixed = fixRange(period);

    if (fixed) {
      requestParms.from = fixed.from;
      requestParms.to = fixed.to;
    }


    const earnings = () => ctx.$store.dispatch('getDashboardEarnings');
    const summary = () => ctx.$store.dispatch('getReportDashboardSummary', requestParms);

    const data = await Promise.all([summary(), earnings()]);

    return data[0];
  }
}

const tableFlowMixin = tableFlowMixinGenerator({
  defaultParams: {
    ...defaultParams,
  },
  params: Object.keys(defaultParams),
  requestOmit: ['chartPeriod', 'miniChartPeriod'],
  fetch,
  period: 'param',
  windowRefreshHook: true,
});


export default {
  // name: 'DashboardIndex',
  name: 'Index',
  components: {
    Charts,
    TableCard,
  },
  mixins: [
    UrlMixin,
    UrlMixinHooks,
    DimensionsMixin,
    InjectPlugins,
    pageSizeMixin,
    tableFlowMixin,
    formatCurrency,
  ],
  fetch,
  props: ['fetchedData', 'passedParams'],
  data() {
    return {
      viewName,
      pageLimitProp,
      dashboardColumns,
      loading: false,
      pagination: true,
      count: 0,
      formaters: Formatters,
      chartPeriod: '',
      miniChartPeriod: '',
      lastQuery: {},
      unwatch: Function,
      columns: [
        {
          name: 'date_formatted',
          source: 'dimensions',
          filterable: false,
          sortable: 'custom',
        }, {
          name: 'impressions_count',
          source: 'metrics',
          filterable: false,
          sortable: 'custom',
          align: 'right',
        }, {
          name: 'visits_count',
          source: 'metrics',
          filterable: false,
          sortable: 'custom',
          align: 'right',
        }, {
          name: 'registrations_count',
          source: 'metrics',
          filterable: false,
          sortable: 'custom',
          align: 'right',
        }, {
          name: 'deposits_first_count',
          source: 'metrics',
          filterable: false,
          sortable: 'custom',
          align: 'right',
        }, {
          name: 'deposits_all_count',
          source: 'metrics',
          filterable: false,
          sortable: 'custom',
          align: 'right',
        }, {
          name: 'deposits_all_sum',
          source: 'metrics',
          filterable: false,
          sortable: 'custom',
          align: 'right',
        }, {
          name: 'net_profit_sum',
          source: 'metrics',
          filterable: false,
          sortable: 'custom',
          align: 'right',
        }, {
          name: 'revenue_sum',
          source: 'metrics',
          filterable: false,
          sortable: 'custom',
          align: 'right',
        },
      ],
    };
  },
  computed: {
    currencyLabel() {
      return this.getCurrencyLabel(this.$store.state.payments.currencyCode);
    },
    available() {
      if (this.$store.state.payments) {
        return this.formatPrice(this.$store.state.payments.availableBalance);
      }

      return '';
    },
    yesterday() {
      if (this.$store.state.report.earnings) {
        return this.formatPrice(this.$store.state.report.earnings.yesterday);
      }

      return '';
    },
    thisMonth() {
      if (this.$store.state.report.earnings) {
        return this.formatPrice(this.$store.state.report.earnings.this_month);
      }

      return '';
    },
    lastMonth() {
      if (this.$store.state.report.earnings) {
        return this.formatPrice(this.$store.state.report.earnings.last_month);
      }

      return '';
    },
    total() {
      if (this.$store.state.report.earnings) {
        return this.formatPrice(this.$store.state.report.earnings.total);
      }

      return '';
    },
    rows() {
      return this.$store.state.report.dashboard_reports
        .map((e) => this.$_.omit(e, [
          'row_id',
          'row_filter_column',
          'row_filter_column',
        ]));
    },
    firstDay() {
      return this.$store.state.profile.user.first_day_of_week;
    },
  },
  watch: {
    '$i18n.locale': async function () {
      if (this.isComponentActivated) {
        const promise = Promise.all([
          this.fetchData(this.$_.cloneDeep(this.currentQuery)),
          this.$store.dispatch('getSettings'),
        ]);

        await promise;
        await this.afterBrowserRedraw();
        this.loading = false;
      }
    },
  },
  methods: {
    async refresh() {
      this.loading = true;
      await this.fetchData(this.$_.cloneDeep(this.currentQuery));
      this.$refs.charts.$emit('refresh');
      await this.afterBrowserRedraw();
      this.loading = false;
    },
    cacheChart(params) {
      if (params.type === 'normal') {
        this.chartPeriod = params.period;
        this.pushQuery(this.$route, this.cacheData);
      } else {
        this.miniChartPeriod = params.period;
        this.pushQuery(this.$route, this.cacheData);
      }
    },
    formatPrice(amount) {
      const sum = this.$filters.currency(amount, this.currencyLabel, null, {
        symbolOnLeft: true,
        spaceBetweenAmountAndSymbol: false,
        decimalSeparator: ',',
      });

      if (amount < 0 && Math.abs(amount) >= 0.005) {
        return `<span class="text-red">${sum}</span>`;
      }

      if (amount > 0 && Math.abs(amount) >= 0.005) {
        return `<span class="text-green">${sum}</span>`;
      }

      return `<span>${sum.replace('-', '')}</span>`;
    },
  },
  created() {
    this.setFromQuery(this.passedParams);
  },
  activated() {
    this.fetchData(null, this.lastQuery);
  },
};
</script>

<style lang="scss">
// vue3-migration -- '@/assets/theme/common-page-styles.scss'
// @import 'src/assets/theme/default/dashboard/index';
</style>
